import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Card, Col, Form, Modal, Pagination, Row, Toast, ToastContainer } from "react-bootstrap";
import api from "../../../../utils/ApiMethod";
import PageTitle from "../../../../components/PageTitle";
import Spinner from "../../../../components/Spinner";
import Table from "../../../../components/Table";
import StatisticsWidget from "../../../widgets/StatisticsWidget";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SEARCH_DEBOUNCE_MS = 400;
const TOAST_DURATION_MS = 4000;

type ToastState = { variant: "success" | "danger"; message: string } | null;

// api.updatePatchData returns the axios response on success, and on failure
// whatever ApiService's interceptor rejected with: { status, message } when
// the server answered, or the raw axios error when it couldn't be reached.
const getResetErrorMessage = (response: any): string => {
  // 401/440 come from SuperiorAuthentication when the login/session is stale.
  if (response?.status === 401 || response?.status === 440) {
    return "Your session has expired. Please log in again and retry.";
  }
  if (typeof response?.status === "number" && response?.message) return response.message;
  if (response?.data?.message) return response.data.message;
  if (response?.isAxiosError || response?.request) {
    return "Unable to reach the server. Please check your internet connection and try again.";
  }
  return "Could not reset the password. Please try again.";
};

const SchoolNotResetTheirPassword = () => {
  // The reset route is guarded by SuperiorAuthentication/SuperiorAuthorisation,
  // which need the logged-in admin's id and session key in the URL.
  const user = useSelector((state: any) => state.Auth.user);
  const profileId = user?._id || user?.user?._id || "";
  const Key_Key = user?.moniterd_ttl || user?.user?.moniterd_ttl || "";

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [overallNotReset, setOverallNotReset] = useState(0);
  const [totalSchools, setTotalSchools] = useState(0);

  const [resetTarget, setResetTarget] = useState<{ Udise_Code: string; School_Name: string } | null>(null);
  const [resetting, setResetting] = useState(false);
  // A single toast slot (not a queue) - a new message just replaces the old one.
  const [toast, setToast] = useState<ToastState>(null);

  // Debounce the search box so every keystroke doesn't fire a request,
  // and jump back to page 1 whenever the search term actually changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchSchools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search) params.set("search", search);

      const response = await api.fetchDataAuth(
        `/school/not/reset/pass?${params.toString()}`
      );
      const schools = response?.data?.schools || [];
      const responseTotal = response?.data?.totalCount ?? 0;
      setData(schools);
      setTotal(responseTotal);
      setTotalPages(response?.data?.totalPages ?? 1);
      setTotalSchools(response?.data?.totalSchools ?? 0);
      setOverallNotReset(response?.data?.notResetCount ?? 0);
    } catch (err) {
      console.error("Error fetching school list:", err);
      setError("Failed to fetch details. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  const handleConfirmReset = async () => {
    if (!resetTarget) return;
    if (!profileId || !Key_Key) {
      setToast({ variant: "danger", message: "Your session has expired. Please log in again and retry." });
      return;
    }
    setResetting(true);
    try {
      const response = await api.updatePatchData(
        `/school/reset/password/${encodeURIComponent(resetTarget.Udise_Code)}/${profileId}/${Key_Key}`,
        {}
      );
      if (response?.data?.status) {
        // Flip just this row's status locally instead of refetching the
        // whole page - the row's own passwordReset is the only thing that
        // changed. Only decrement the "not reset" tile if this row was
        // actually counted as not-reset before (don't double-count a row
        // that was already marked Reseted Password).
        const wasNotReset = !data.find((row) => row.Udise_Code === resetTarget.Udise_Code)?.passwordReset;
        setData((prev) =>
          prev.map((row) =>
            row.Udise_Code === resetTarget.Udise_Code
              ? { ...row, passwordReset: true, resetCount: (row.resetCount || 0) + 1 }
              : row
          )
        );
        if (wasNotReset) setOverallNotReset((prev) => Math.max(prev - 1, 0));
        setResetTarget(null);
        setToast({
          variant: "success",
          message: response.data.message || `Password for ${resetTarget.School_Name} has been reset.`,
        });
      } else {
        setToast({ variant: "danger", message: getResetErrorMessage(response) });
      }
    } catch (err) {
      setToast({ variant: "danger", message: getResetErrorMessage(err) });
    } finally {
      setResetting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "S.NO",
        accessor: "index",
        Cell: ({ row }: { row: any }) => (
          <span className="text-muted">
            {(page - 1) * limit + row.index + 1}
          </span>
        ),
        sort: false,
      },
      {
        Header: "Udise Code",
        accessor: "Udise_Code",
        sort: false,
      },
      {
        Header: "School Name",
        accessor: "School_Name",
        sort: false,
      },
      {
        Header: "Email",
        accessor: "currentEmailId",
        sort: false,
        Cell: ({ row }: { row: any }) => (
          <span style={{ fontWeight: 600 }}>{row.original.currentEmailId || "-"}</span>
        ),
      },
      {
        Header: "Cluster Name",
        accessor: "clusterName",
        sort: false,
        Cell: ({ row }: { row: any }) => row.original.clusterName || "-",
      },
      {
        Header: "Block Name Code",
        accessor: "Block_Name_Code",
        sort: false,
      },
      {
        Header: "School District",
        accessor: "District_Name_Code",
        sort: false,
      },
      {
        Header: "Status Of Password Reset",
        accessor: "passwordReset",
        sort: false,
        Cell: ({ row }: { row: any }) => {
          return row?.original?.passwordReset ? (
            <span style={{ color: "green", fontWeight: "bold" }}>
              Reseted Password
            </span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>
              Password Not Reset
            </span>
          );
        },
      },
      {
        Header: "Reset Count",
        accessor: "resetCount",
        sort: false,
        Cell: ({ row }: { row: any }) => row.original.resetCount ?? 0,
      },
      {
        Header: "Action",
        accessor: "action",
        sort: false,
        Cell: ({ row }: { row: any }) => (
          <Button
            variant="outline-danger"
            size="sm"
            // disabled={!!row.original.passwordReset}
            onClick={() =>
              setResetTarget({
                Udise_Code: row.original.Udise_Code,
                School_Name: row.original.School_Name,
              })
            }
          >
            Reset Password
          </Button>
        ),
      },
    ],
    [page, limit]
  );

  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);

  // A short, centered window of page numbers instead of one item per page —
  // matters once there are thousands of pages across 38k+ schools.
  const pageWindow = useMemo(() => {
    const windowSize = 5;
    let start = Math.max(1, page - Math.floor(windowSize / 2));
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const items = [];
    for (let p = start; p <= end; p++) items.push(p);
    return items;
  }, [page, totalPages]);

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: "All School List", path: "/school/list" },
          {
            label: "All School List",
            path: "/school/list",
            active: true,
          },
        ]}
        title={"All School list"}
      />

      <Row>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="primary"
            title="Total School List"
            stats={totalSchools.toLocaleString()}
            icon="home"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="success"
            title="Password Reset"
            stats={Math.max(0, totalSchools - overallNotReset).toLocaleString()}
            icon="unlock"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="danger"
            title="Password Not Reset"
            stats={overallNotReset.toLocaleString()}
            icon="lock"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="warning"
            title="Current Page"
            stats={`${page} / ${totalPages}`}
            icon="file-text"
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Body>
              {error && <div className="alert alert-danger py-2">{error}</div>}

              <div className="alert alert-info py-2">
                Resetting a school's password from the Action column sets it to the default password{" "}
                <strong>Mashaal@2026</strong>.
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <Form.Control
                  type="search"
                  placeholder="Search by Udise Code, School, Block or District..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-auto"
                  style={{ minWidth: 320 }}
                />
                <Form.Select
                  value={limit}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="w-auto"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </Form.Select>
              </div>

              {loading ? (
                <div style={{ textAlign: "center" }}>
                  <Spinner />
                </div>
              ) : (
                <>
                  <Table
                    columns={columns}
                    data={data}
                    isSortable={false}
                    pagination={false}
                    isSearchable={false}
                  />

                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                    <span className="text-muted small">
                      {total === 0
                        ? "No schools found"
                        : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
                    </span>
                    {totalPages > 1 && (
                      <Pagination className="mb-0">
                        <Pagination.First
                          disabled={page === 1}
                          onClick={() => setPage(1)}
                        />
                        <Pagination.Prev
                          disabled={page === 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        />
                        {pageWindow.map((p) => (
                          <Pagination.Item
                            key={p}
                            active={p === page}
                            onClick={() => setPage(p)}
                          >
                            {p}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          disabled={page === totalPages}
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                        />
                        <Pagination.Last
                          disabled={page === totalPages}
                          onClick={() => setPage(totalPages)}
                        />
                      </Pagination>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={!!resetTarget} onHide={() => (resetting ? null : setResetTarget(null))} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">
            Are you sure you want to reset the password for{" "}
            <strong>{resetTarget?.School_Name}</strong> ({resetTarget?.Udise_Code})?
          </p>
          <p className="text-muted small mb-0">
            The school's password will be set to the default password <strong>Mashaal@2026</strong>, and
            it will be marked as "Reseted Password".
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={resetting} onClick={() => setResetTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={resetting} onClick={handleConfirmReset}>
            {resetting ? "Resetting..." : "Reset Password"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-end" className="p-3" style={{ position: "fixed", zIndex: 2000 }}>
        <Toast
          show={!!toast}
          onClose={() => setToast(null)}
          delay={TOAST_DURATION_MS}
          autohide
          bg={toast?.variant}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toast?.variant === "success" ? "Password reset" : "Reset failed"}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast?.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default SchoolNotResetTheirPassword;
